import { createContext, useContext, useState, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import DownloadService from "services/download-service";

const PlatformScoutContext = createContext();

export function PlatformScoutProvider({ children }) {
    // ---------------- Search Persistence ----------------
    const [searchState, setSearchState] = useState({
        searchTerm: "",
        results: [],
        trendingResults: [],
        hasSearched: false,
        error: "",
        shouldPersist: false, // [NEW] Default: don't persist
    });

    const updateSearchState = (newState) => {
        setSearchState((prev) => ({ ...prev, ...newState }));
    };

    const resetSearch = () => {
        setSearchState({
            searchTerm: "",
            results: [],
            trendingResults: [],
            hasSearched: false,
            error: "",
            shouldPersist: false,
        });
    };

    const clearIfNotPersisted = () => {
        // Use functional update to check the LATEST state value
        setSearchState((current) => {
            if (!current.shouldPersist) {
                return {
                    searchTerm: "",
                    results: [],
                    trendingResults: [], // Maybe keep trending? User said "old results should not be retained". Usually trending is separate. Let's reset everything like resetSearch does to be safe, or just results. The user said "if the user searched... then came back the old results should not be retained". Trending is usually loaded on mount anyway.
                    hasSearched: false,
                    error: "",
                    shouldPersist: false,
                };
            }
            return current;
        });
    };

    // ---------------- Global Download Logic ----------------
    // Items map: { [uniqueKey]: { progress, profileData, error, isComplete } }
    const [downloadState, setDownloadState] = useState({
        items: {},
        showToast: false,
    });

    // Store multiple WebSockets: { [uniqueKey]: WebSocket }
    const wsRefs = useRef({});

    const closeToast = () => {
        setDownloadState(prev => ({ ...prev, showToast: false }));

        // Optional: Cleanup completed/errored items from state after delay?
        // For now, we leave them so checks in SearchCard still work (isInSocialStash sync).
        // But maybe we want to remove them from the TOAST list?
        // The user logic for "persistence" relies on state.
    };

    const startDownload = async (profileData, fetchUsageCallback) => {
        const key = `${profileData.platform}_${profileData.id}`;

        // Prevent duplicate download for SAME profile
        if (downloadState.items[key]?.isDownloading) return;

        // [NEW] Mark search as persistent
        updateSearchState({ shouldPersist: true });

        // Update State: Add/Reset this item
        setDownloadState((prev) => ({
            ...prev,
            showToast: true,
            items: {
                ...prev.items,
                [key]: {
                    isDownloading: true,
                    progress: 0,
                    profileData,
                    error: null,
                    isComplete: false,
                }
            }
        }));

        const clientId = `client-${Date.now()}`;
        const wsBaseUrl = process.env.REACT_APP_API_URL.replace(/^http/, "ws");

        try {
            const ws = new WebSocket(`${wsBaseUrl}/ws?clientId=${clientId}`);
            wsRefs.current[key] = ws;

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.progress !== undefined) {
                        setDownloadState((prev) => ({
                            ...prev,
                            items: {
                                ...prev.items,
                                [key]: {
                                    ...prev.items[key],
                                    progress: Math.floor(data.progress)
                                }
                            }
                        }));
                    }
                } catch (err) {
                    console.warn("⚠️ Invalid WebSocket message:", event.data);
                }
            };

            ws.onerror = (err) => {
                console.error("❌ WebSocket error:", err);
                setDownloadState((prev) => ({
                    ...prev,
                    items: {
                        ...prev.items,
                        [key]: {
                            ...prev.items[key],
                            isDownloading: false,
                            error: "Connection Error"
                        }
                    }
                }));
            };

            ws.onclose = () => {
                // Cleanup ref
                delete wsRefs.current[key];
            };

            ws.onopen = async () => {
                try {
                    const payload = { ...profileData, clientId };
                    await DownloadService.downloadProfile(profileData.platform, { profileData: payload });

                    // Success
                    setDownloadState((prev) => ({
                        ...prev,
                        items: {
                            ...prev.items,
                            [key]: {
                                ...prev.items[key],
                                isDownloading: false,
                                isComplete: true,
                                progress: 100
                            }
                        }
                    }));

                    if (fetchUsageCallback) await fetchUsageCallback();

                } catch (error) {
                    console.error("Error saving profile:", error);
                    let errorMsg = "Download Failed";
                    if (error.response?.status === 403 || error.message?.toLowerCase().includes("limit")) {
                        errorMsg = "Limit Exceeded";
                    }

                    setDownloadState((prev) => ({
                        ...prev,
                        items: {
                            ...prev.items,
                            [key]: {
                                ...prev.items[key],
                                isDownloading: false,
                                error: errorMsg
                            }
                        }
                    }));
                } finally {
                    if (wsRefs.current[key]) {
                        wsRefs.current[key].close();
                    }
                }
            };

        } catch (error) {
            console.error("WebSocket setup error:", error);
            setDownloadState((prev) => ({
                ...prev,
                items: {
                    ...prev.items,
                    [key]: {
                        ...prev.items[key],
                        isDownloading: false,
                        error: "Setup Error"
                    }
                }
            }));
        }
    };

    const value = useMemo(
        () => ({
            ...searchState,
            updateSearchState,
            resetSearch,
            clearIfNotPersisted, // [NEW]
            downloadState,
            startDownload,
            closeToast,
        }),
        [searchState, downloadState]
    );

    return <PlatformScoutContext.Provider value={value}>{children}</PlatformScoutContext.Provider>;
}

// Hook
export function usePlatformScout() {
    const context = useContext(PlatformScoutContext);
    if (!context) {
        throw new Error("usePlatformScout must be used within a PlatformScoutProvider");
    }
    return context;
}

PlatformScoutProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
